import { tw } from "@careinfo/tailwind";
import {
  Combobox as ComboboxWrapper,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Transition,
} from "@headlessui/react";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Control, Controller, FieldPath, FieldValues } from "react-hook-form";
import { useDebounce } from "../../hooks/useDebounceHook";
import useGetHook from "../../hooks/useGetHook";

interface Props<T extends FieldValues> {
  label: string;
  queryKey: string;
  url: string;
  name: FieldPath<T>;
  control: Control<T, any>;
  params?: Record<string, any>;
  placeholder?: string;
  limit?: number;
  dataLabel?: string;
}

export default function Combobox<T extends FieldValues>({
  url,
  queryKey,
  name,
  control,
  limit = 10,
  params = {},
  label = "Async Select",
  placeholder = "Select ....",
  dataLabel = "name",
}: Props<T>) {
  const [query, setQuery] = useState("");
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const comboBoxRef = useRef<HTMLDivElement | null>(null);
  const debounceSearch = useDebounce(query);

  const { data: serverData } = useGetHook<any>({
    queryKey: [queryKey, `${queryKey}-${limit}-${debounceSearch}`],
    url: url,
    params: {
      limit: limit,
      offset: 0,
      search: debounceSearch,
      ...params,
    },
  });

  const filteredData = useMemo(() => {
    const dataToFilter = serverData?.results ?? [];
    return query === ""
      ? dataToFilter
      : dataToFilter.filter((item: any) =>
          item?.[dataLabel]
            ?.toLowerCase()
            ?.replace(/\s+/g, "")
            ?.includes(query.toLowerCase().replace(/\s+/g, ""))
        );
  }, [query, serverData?.results, dataLabel]);

  const handleQueryChange = useCallback((event: any) => {
    setQuery(event.target.value);
    setIsOpen(true);
  }, []);

  const handleToggleOpen = useCallback(
    (e: any) => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    },
    [isOpen]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        comboBoxRef.current &&
        !comboBoxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [comboBoxRef]);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={null}
      render={({ field, fieldState: { error } }) => {
        return (
          <ComboboxWrapper
            value={field.value}
            onChange={(value) => {
              field.onChange(value);
              setIsOpen(false);
            }}
          >
            <div ref={comboBoxRef} className={tw`relative`}>
              <label htmlFor={name}>{label}</label>
              <div
                className={tw`relative w-full cursor-default overflow-hidden bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm mt-2`}
              >
                <ComboboxInput
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-[10px] text-left`}
                  placeholder={placeholder}
                  autoComplete="off"
                  displayValue={(item: any) => item?.[dataLabel]}
                  onChange={handleQueryChange}
                  onClick={() => setIsOpen(true)}
                  id={name}
                />
                <ComboboxButton
                  className={tw`absolute inset-y-0 right-0 flex items-center pr-2`}
                  onClick={handleToggleOpen}
                >
                  {field.value && (
                    <PlusIcon
                      className={tw`h-5 w-5 text-gray-400 rotate-45`}
                      aria-hidden="true"
                      onClick={(e) => {
                        e.stopPropagation();
                        field.onChange(null);
                      }}
                    />
                  )}
                  <ChevronDownIcon
                    className={tw`h-5 w-5 text-gray-400`}
                    aria-hidden="true"
                    onClick={handleToggleOpen}
                  />
                </ComboboxButton>
              </div>
              <Transition
                as={Fragment}
                show={isOpen}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery("")}
              >
                <ComboboxOptions
                  className={tw`absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
                >
                  {filteredData?.length === 0 && query !== "" ? (
                    <div
                      className={tw`relative cursor-default select-none py-2 px-4 text-gray-700`}
                    >
                      Nothing found.
                    </div>
                  ) : (
                    filteredData?.length > 0 &&
                    filteredData.map((data: any) => (
                      <ComboboxOption
                        key={data.id}
                        className={({ active }) =>
                          tw`relative hover:bg-primary-light hover:text-white hover:cursor-pointer cursor-default select-none py-2 pl-10 pr-4 ${
                            active
                              ? tw`bg-primary text-white`
                              : tw`text-gray-900`
                          }`
                        }
                        value={data}
                      >
                        {({ selected, active }) => (
                          <>
                            <span
                              className={tw`block truncate ${
                                selected ? tw`font-medium` : tw`font-normal`
                              }`}
                            >
                              {data?.[dataLabel] ?? "N/A"}
                            </span>
                            {selected ? (
                              <span
                                className={tw`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                  active ? tw`text-white` : tw`text-teal-600`
                                }`}
                              >
                                {/* <CheckIcon className="h-5 w-5" aria-hidden="true" /> */}
                              </span>
                            ) : null}
                          </>
                        )}
                      </ComboboxOption>
                    ))
                  )}
                </ComboboxOptions>
              </Transition>
              {error && (
                <p className={tw`text-red-500 text-xs my-1`}>
                  {JSON.stringify(error?.message)}
                </p>
              )}
            </div>
          </ComboboxWrapper>
        );
      }}
    />
  );
}
